import React, { useEffect, useState } from 'react';
import styles from './FileSelector.module.css';
import DragAndDrop from '../general-components/drag-and-drop';
import DropdownSelect, { Option } from '../general-components/DropdownSelect';
import {
    getUploadUrl,
    uploadFile,
    getUserFiles,
    deleteFile,
    getManufacturerFiles,
    getDepartmentFiles,
    getNIImplantUploadUrl,
    getNIUploadUrl,
    getNordinsightFiles,
} from '../../services/api/fileService';
import BackgroundContainer from '../ui-components/BackgroundContainer';
import Alert from '../ui-components/Alert';
import { getFileMimeType, useValidateFile } from '../../utils/files';
import LoadingRing from '../ui-components/LoadingRing';
import FileModal from '../video-overview/FileModal';
import FileCard from './FileCard';
import { FileType } from './types';

interface FileSelectorProps {
    defaultFiles?: any[];
    onFilesSelected: (fileIds: string[]) => void;
    ownerType?: 'department' | 'manufacturer' | 'user' | 'nordinsight';
    allowMultiple?: boolean;
    allowedTypes?: {
        document?: boolean;
        image?: boolean;
        video?: boolean;
    };
    label?: string;
    manufacturerName?: string;
    disabled?: boolean;
    disabledMessage?: string;
    isEditable?: boolean;
}

const FileSelector: React.FC<FileSelectorProps> = ({
    defaultFiles,
    onFilesSelected,
    ownerType = 'user',
    allowMultiple = false,
    allowedTypes = { document: true, image: true, video: false },
    label = 'Select files',
    manufacturerName,
    disabled = false,
    disabledMessage = 'File selection is currently disabled',
    isEditable = false,
}) => {
    const [userFiles, setUserFiles] = useState<FileType[]>([]);
    const [selectedFiles, setSelectedFiles] = useState<string[]>(
        defaultFiles || []
    );

    const [isUploading, setIsUploading] = useState(false);
    const [shownFile, setShownFile] = useState<FileType | null>(null);

    const [errors, addError, clearErrors, removeError, validateFile] =
        useValidateFile(allowedTypes);

    // Filter files for dropdown selection
    const getFilteredFiles = () => {
        return userFiles.filter((file) => {
            if (file.ownerType === ownerType) {
                return (
                    (file.documentType === 'document' &&
                        allowedTypes.document) ||
                    (file.documentType === 'image' && allowedTypes.image) ||
                    (file.documentType === 'video' && allowedTypes.video)
                );
            }
            return false;
        });
    };

    // Fetch user files from the API
    const fetchFiles = async () => {
        let files: any[] = [];
        try {
            if (ownerType === 'manufacturer' && manufacturerName) {
                files = await getManufacturerFiles(manufacturerName);
            } else if (ownerType === 'department') {
                files = await getDepartmentFiles();
            } else if (ownerType === 'nordinsight') {
                files = await getNordinsightFiles();
            } else if (ownerType === 'user') {
                files = await getUserFiles();
            } else {
                return;
            }
            // Ensure files is an array
            if (!Array.isArray(files)) {
                files = [];
            }
            const filteredFiles = files.filter((file: any) => {
                return allowedTypes[
                    file.documentType as keyof typeof allowedTypes
                ];
            });
            setUserFiles(filteredFiles);
            setSelectedFiles(
                selectedFiles.filter((file) =>
                    filteredFiles.some((f: any) => f._id === file)
                )
            );
        } catch (error) {
            addError({
                fileName: 'Error fetching files',
                errors: [(error as Error).message],
            });
        }
    };

    // Fetch user files on component mount
    useEffect(() => {
        fetchFiles();
    }, [manufacturerName]);

    // Handle file upload with progress tracking
    const handleFileUpload = async (files: FileList) => {
        if (!files) return;
        if (errors.length > 0) return;
        clearErrors();

        const filesArray = Array.from(files);
        for (const f of filesArray) {
            setIsUploading(true);

            const isValid = validateFile(f);
            if (!isValid) {
                setIsUploading(false);
                continue;
            }

            let url, file;

            if (ownerType === 'manufacturer') {
                if (manufacturerName) {
                    try {
                        const uploadInfo = await getNIImplantUploadUrl({
                            ownerType,
                            fileType: getFileMimeType(f),
                            filename: f.name,
                            manufacturerName,
                        });
                        url = uploadInfo.url;
                        file = uploadInfo.file;
                    } catch (error) {
                        addError({
                            fileName: f.name,
                            errors: ['Error getting upload URL'],
                        });
                        setIsUploading(false);
                        continue;
                    }
                } else {
                    addError({
                        fileName: f.name,
                        errors: ['Manufacturer name is required'],
                    });
                    setIsUploading(false);
                    continue;
                }
            } else if (ownerType === 'nordinsight') {
                const uploadInfo = await getNIUploadUrl({
                    ownerType,
                    fileType: getFileMimeType(f),
                    filename: f.name,
                    manufacturerName,
                });
                url = uploadInfo.url;
                file = uploadInfo.file;
            } else {
                try {
                    const uploadInfo = await getUploadUrl({
                        ownerType,
                        filename: f.name,
                        fileType: getFileMimeType(f),
                    });
                    url = uploadInfo.url;
                    file = uploadInfo.file;
                } catch (error) {
                    addError({
                        fileName: f.name,
                        errors: ['Error getting upload URL'],
                    });
                    setIsUploading(false);
                    continue;
                }
            }

            if (!url) {
                addError({
                    fileName: f.name,
                    errors: ['Failed to get upload URL. You may not have permission to upload files.'],
                });
                setIsUploading(false);
                continue;
            }

            // Upload file to the URL
            const response = await uploadFile(url, f);

            if (response === 'OK') {
                // Refresh file list after successful upload
                await fetchFiles();

                // Select the newly uploaded file
                if (file && file._id) {
                    handleFileSelection(file._id);
                }
            } else {
                if (file && file._id) {
                    deleteFile(file._id);
                }
                addError({
                    fileName: f.name,
                    errors: ['Error uploading file:' + response],
                });
            }
            setIsUploading(false);
        }
    };

    // Handle selection of files from the dropdown
    const handleFileSelection = (fileId: string) => {
        let newSelectedFiles: string[];

        if (selectedFiles.includes(fileId)) {
            // If already selected, remove it
            newSelectedFiles = selectedFiles.filter((id) => id !== fileId);
        } else {
            if (allowMultiple) {
                newSelectedFiles = [...selectedFiles, fileId];
            } else {
                newSelectedFiles = [fileId];
            }
        }

        setSelectedFiles(newSelectedFiles);
        onFilesSelected(newSelectedFiles);
    };

    // Handle file changes from FileCard
    const handleFileChange = (id: string, updatedFile: FileType) => {
        setUserFiles((prevFiles) =>
            prevFiles.map((file) => (file._id === id ? updatedFile : file))
        );
    };

    return (
        <BackgroundContainer>
            {shownFile && (
                <FileModal
                    file={{ ...shownFile, title: shownFile.filename }}
                    close={() => setShownFile(null)}
                />
            )}
            <div className={styles.header}>
                <h3>{label}</h3>
            </div>
            {errors.length > 0 && (
                <div className={styles.errors}>
                    {errors.map((error) => (
                        <Alert
                            key={error.fileName}
                            title={error.fileName}
                            variant="destructive"
                            onClose={() => removeError(error.fileName)}
                        >
                            {error.errors.map((error) => (
                                <p key={error}>- {error}</p>
                            ))}
                        </Alert>
                    ))}
                </div>
            )}

            {disabled && (
                <div className={styles.disabledMessage}>
                    <p>{disabledMessage}</p>
                </div>
            )}

            {!disabled && (
                <>
                    <div className={styles.fileSelectionLayout}>
                        {/* Upload section */}
                        <div className={styles.uploadSection}>
                            <h6>Upload New File</h6>
                            <DragAndDrop
                                dropHandler={handleFileUpload}
                                useContainerStyle={true}
                                multiple={allowMultiple}
                            >
                                {isUploading && <LoadingRing size="small" />}
                            </DragAndDrop>
                        </div>

                        {/* Existing files section */}
                        <div className={styles.existingFilesSection}>
                            <h6>Select Existing Files</h6>
                            {getFilteredFiles().length > 0 ? (
                                <DropdownSelect
                                    isSearchable={true}
                                    isMulti={allowMultiple}
                                    name="Files"
                                    labelled={true}
                                    options={getFilteredFiles().reduce(
                                        (acc, file) => {
                                            acc[file._id] = {
                                                checked: selectedFiles.includes(
                                                    file._id
                                                ),
                                                label: file.filename,
                                            } as Option;
                                            return acc;
                                        },
                                        {} as Record<string, Option>
                                    )}
                                    onChange={(
                                        selected: Record<string, Option>
                                    ) => {
                                        const filteredFiles =
                                            getFilteredFiles();
                                        const selectedFileIds = filteredFiles
                                            .filter((file) =>
                                                Object.entries(selected).some(
                                                    ([key, value]) =>
                                                        key === file._id &&
                                                        (value as Option)
                                                            .checked
                                                )
                                            )
                                            .map((file) => file._id);
                                        setSelectedFiles(selectedFileIds);
                                        onFilesSelected(selectedFileIds);
                                    }}
                                />
                            ) : (
                                <p className={styles.noFilesMessage}>
                                    No files available. Upload new files first.
                                </p>
                            )}
                        </div>
                    </div>

                    {selectedFiles.length > 0 && (
                        <div className={styles.selectedFilesPreview}>
                            <h6>Selected files:</h6>
                            <div className={styles.fileCards}>
                                {selectedFiles.map((fileId) => {
                                    const file = userFiles.find(
                                        (f) => f._id === fileId
                                    );
                                    if (file) {
                                        return (
                                            <FileCard
                                                key={file._id}
                                                file={file}
                                                isEditable={isEditable}
                                                onChange={handleFileChange}
                                                onRemoveFile={
                                                    handleFileSelection
                                                }
                                                onShowFile={setShownFile}
                                            />
                                        );
                                    }
                                    return null;
                                })}
                            </div>
                        </div>
                    )}
                </>
            )}
        </BackgroundContainer>
    );
};

export default FileSelector;
